import { CommonModule, TitleCasePipe, UpperCasePipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { Pagination } from '@core/models/pagination.model';
import { LoadingService } from '@core/services/loading.service';
import { MutualFundService } from '@core/services/mutual-fund.service';
import { TableNavigatorComponent } from '@shared/ui/table-navigator/table-navigator.component';
import { TableSkeletonLoaderComponent } from '@shared/ui/table-skeleton-loader/table-skeleton-loader.component';
import { IFILTER } from 'app/app.constants';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { NotificationFacade } from '@core/facades/notification.facade';

import {
  DropdownComponent,
  DropDownModel,
} from '@shared/ui/dropdown/dropdown.component';
import { ConfirmDialogComponent } from '@shared/ui/confirm-dialog/confirm-dialog.component';
import { StatusComponent } from '@shared/ui/status/status.component';
import { ConfirmDialogService } from '@core/services/confirm-dialog.service';
import { PermissionDirective } from '@shared/directives/permission.directive';
import { RolesAndPermissionsService } from '@core/services/roles-and-permissions.service';
@Component({
  selector: 'app-transactions-mutual-funds',
  standalone: true,

  imports: [
    CommonModule,
    DropdownComponent,
    ConfirmDialogComponent,
    TableSkeletonLoaderComponent,
    TableNavigatorComponent,
    RouterOutlet,
    StatusComponent,
    PermissionDirective,
  ],
  templateUrl: './transactions-mutual-funds.component.html',
  styleUrl: './transactions-mutual-funds.component.scss',
  providers: [TitleCasePipe, UpperCasePipe]
})
export class TransactionsMutualFundsComponent implements OnInit, OnDestroy {
  private mutualFundService = inject(MutualFundService);
  private change = inject(ChangeDetectorRef);
  private loadingService = inject(LoadingService);
  private activatedRoute = inject(ActivatedRoute);
  private notify = inject(NotificationFacade);
  private router = inject(Router);
  private confirmDialogService = inject(ConfirmDialogService);
  private permissionService = inject(RolesAndPermissionsService);

  isApproveDialogOpen: boolean = false;
  isApproveInProgress: boolean = false;
  approveDialogQuestion!: string;
  disableApproveAction: boolean = false;


  transactionId: string | null = null;
  accountId: string | null = null;
  beneficiaryDetails: any = null;

  subs: Subscription[] = [];
  isFetchingMutualFundsList$!: Observable<boolean>;
  totalCount: number = 0;
  start_date: Date | undefined;
  end_date: Date | undefined;
  searchText: string = '';
  actionList: DropDownModel[] = [];
  pagination: Pagination = new Pagination();

  private mutualFundsListSubject = new BehaviorSubject<any>(null);
  mutualFundsList$ = this.mutualFundsListSubject.asObservable();

  filterStructure: IFILTER[] = [
    {
      filterKey: 'date',
      label: 'Date',
      type: 'date',
    },
  ];

  constructor(private titleCasePipe: TitleCasePipe, private upperCasePipe: UpperCasePipe) {
    this.isFetchingMutualFundsList$ = this.loadingService.getLoadingObservable(
      'get-mutual-funds-transactions-list'
    );
  }

  resetDialogState(): void {
    this.transactionId = null;
    this.accountId = null;
    this.approveDialogQuestion = '';
    this.beneficiaryDetails = null;
  }

  initDropdownList(fund: any): DropDownModel[] {
    this.actionList = [];

      this.actionList.push(
        {
          action: () => {
            this.prepareDeleteDialog(fund);
          },
          title: 'Delete Transaction',
          show: true && this.permissionService.hasPermission(['can_delete_sub_rem']),
        },
        {
          action: () => {
             this.confirmDialogService.openDialog({
              isOpen: true,
              isDialogLoading: false,
              title: 'Yes, Retry Transaction',
              description: 'Are you sure you want to retry this transaction?',
              loadingText: 'Seeding Transaction, Please Wait...',
              action: () => this.retryDeluxeSeeding(fund.id),
              onClose: () => {
                this.resetDialogState()
              },
            });
            this.retryDeluxeSeeding(fund.id);
          },
          title: 'Retry Deluxe seeding',
          show: fund.delux_status != 'seeded' && this.permissionService.hasPermission(['can_retry_deluxe_mfd_seeding']),
        },
      );
    return this.actionList;
  }


  ngOnInit() {
    const sub = this.activatedRoute.queryParams.subscribe((params) => {
      this.searchText = params['search'] || '';
      const page = params['page'] ? +params['page'] : 1;
      if (page) {
        this.pagination.skip = (page - 1) * 10;
        this.pagination.size = 10;
      }
      this.start_date = params['start_date']
        ? new Date(params['start_date'])
        : undefined;
      this.end_date = params['end_date']
        ? new Date(params['end_date'])
        : undefined;

      if (this.start_date && this.end_date) {
        this.getMutualFundsList(
          this.pagination,
          this.searchText,
          this.start_date,
          this.end_date
        );
      } else if (this.searchText) {
        this.getMutualFundsList(this.pagination, this.searchText);
      } else if (!this.searchText && !this.start_date && !this.end_date) {
        this.getMutualFundsList(this.pagination);
      }
    });
    this.subs.push(sub);

  }

  getMutualFundsList(
    page: Pagination,
    search_text: string = '',
    start_date?: Date,
    end_date?: Date
  ) {
    const sub = this.mutualFundService
      .getMutualFundsTransactionsList(page, search_text, start_date, end_date)
      .subscribe({
        next: (res) => {
          this.totalCount = res.data.count;
          this.mutualFundsListSubject.next(res.data.data);
          this.change.detectChanges();
        },
        error: () => {},
      });

    this.subs.push(sub);
  }

  prepareDeleteDialog(fund: any): void {
    this.transactionId = fund.id;
    this.accountId = fund.account_id;

    const accountName =
      fund.name || fund.account_name || fund.user_name || 'N/A';
    const accountNumber = fund.account_number || fund.accountNumber || 'N/A';

    const amount =
      (fund.amount && fund.amount !== 0
        ? fund.amount
        : fund.accrued_principal) / 100;
    const formattedAmount = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
    }).format(amount);

    this.approveDialogQuestion = `
      <div class="text-left mb-4">
      <div class="text-xl font-medium mb-2">Are you sure you want to delete this transaction?</div>
      <div class="text-black-500 font-bold"  >This action cannot be undone.</div>
    </div>
    
    <div class="border-t border-b py-3 px-4 mb-4">
      <div class="text-sm font-bold text-black-500 mb-2">Transaction Details</div>
      
      <div class="grid grid-cols-2 gap-2">
        <div class="text-sm text-gray-500">Account Name:</div>
        <div class="text-sm text-right font-medium">${accountName}</div>
        
        <div class="text-sm text-gray-500">Account Number:</div>
        <div class="text-sm text-right font-medium">${accountNumber}</div>
        
        <div class="text-sm text-gray-500">Amount:</div>
        <div class="text-sm text-right font-medium">${formattedAmount}</div>

        <div class="text-sm text-gray-500">Type:</div>
        <div class="text-sm text-right font-medium">${fund.type}</div>
    `;
    this.disableApproveAction = false;
    this.confirmDialogService.openDialog({
      isOpen: true,
      isDialogLoading: false,
      title: 'Yes, Delete Transaction',
      description: this.approveDialogQuestion,
      loadingText: '"Deleting Transaction, Please Wait...',
      isHTMLQuestion: true,
      disableActionButton: this.disableApproveAction,
      action: () => this.deleteTransaction(),
      onClose: () => {
        this.resetDialogState()
      },
    });
  }


  deleteTransaction() {
    this.confirmDialogService.updateDialog({ isDialogLoading: true });
    this.mutualFundService.deleteTransaction(this.transactionId!).subscribe({
      next: (res) => {
        this.confirmDialogService.updateDialog({
          isDialogLoading: false,
          isOpen: false,
        });
        this.notify.success('Transaction deleted successfully');
        this.getMutualFundsList(
          this.pagination,
          this.searchText,
          this.start_date,
          this.end_date
        );
        this.resetDialogState();
        this.change.detectChanges();
      },
      error: (err) => {
        this.confirmDialogService.updateDialog({
          isDialogLoading: false,
          isOpen: false,
        });
        this.notify.error('Error deleting transaction:', err);
      },
    });
  }

  navigate(currentPage: number) {
    const skip = currentPage * 10 - 10;
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        page: currentPage,
      },
      queryParamsHandling: 'merge',
    });
  }


  retryDeluxeSeeding(transactionId: string) {
    this.confirmDialogService.updateDialog({ isDialogLoading: true });
    this.mutualFundService.retryDeluxeSeeding(transactionId).subscribe({
      next: (res) => {
         this.confirmDialogService.updateDialog({
          isDialogLoading: false,
          isOpen: false,
        });
        this.notify.success('Transaction seeded successfully');
        this.getMutualFundsList( this.pagination, this.searchText, this.start_date, this.end_date );
        this.change.detectChanges();
      },
      error: (err) => {
         this.confirmDialogService.updateDialog({
          isDialogLoading: false,
          isOpen: false,
        });
        this.notify.error('Error seeding transaction:', err);
      },
    });
  }

  formatName(name: string): string {
    if (!name) return '-';
    
    const parts = name.trim().split(' ');
    
    if (parts.length === 0) return '-';
    if (parts.length === 1) return this.titleCasePipe.transform(parts[0]) || '';
    if (parts.length === 2) {
      // Only first and last name
      const firstName = this.titleCasePipe.transform(parts[0]) || '';
      const lastName = this.upperCasePipe.transform(parts[1]) || '';
      return `${firstName} ${lastName}`.trim();
    }
    
    // Three or more parts: first name goes to end in uppercase, rest are title case
    const firstName = this.upperCasePipe.transform(parts[0]) || '';
    const middleAndLastNames = parts.slice(1)
      .map(name => this.titleCasePipe.transform(name))
      .join(' ');
    
    return `${middleAndLastNames} ${firstName}`.trim();
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
